/**
 * Conversion utilities between LN1 composite key strings and FbMessageRef objects.
 *
 * LN1 uses composite keys of the form "<category>:<variant>:<enumValue>"
 * (e.g. "status:normal:7") for per-row message selection. The shared
 * Ln2MappingEditor works with FbMessageRef objects. These utilities allow
 * either representation to be derived from the other.
 *
 * Notes:
 * - deviceType defaults to "ELEVATOR" for composite-key->FbMessageRef conversion
 *   because LN1 composite keys are device-type-agnostic; the actual device type is
 *   resolved at expansion time from the bank context.
 * - variant is inferred from encodedType when converting FbMessageRef->composite key:
 *   OneHotMessage (1) -> "valued", BitMaskMessage (2) -> "normal".
 */
import { FbMessageRef } from "../../../interfaces/FbMessage.interface";
import { LiftType } from "../../../flatbuffers/generated/archive/lift-type";
import {
  parseCompositeKey,
  getDisplayName,
  messageContentToCategoryKey,
  LN2_MESSAGE_CATEGORIES,
} from "../../../flatbuffers/types/ln2MessageOptions";
import { MessageContent } from "../../../flatbuffers/generated/ln2/message-content";
import { EncodedMessageContent } from "../../../flatbuffers/generated/ln2/encoded-message-content";

/**
 * Convert a LN1 composite key (e.g. "status:normal:7") to an FbMessageRef.
 *
 * Returns null when the key is empty, malformed, or refers to an unknown category.
 */
export function compositeKeyToFbRef(key: string): FbMessageRef | null {
  if (!key) return null;
  const parsed = parseCompositeKey(key);
  if (!parsed) return null;
  const category = LN2_MESSAGE_CATEGORIES.find((c) => c.categoryKey === parsed.categoryKey);
  if (!category) return null;
  return {
    encodedType: category.encodedMessageContent,
    messageContent: category.messageContent,
    messageValue: parsed.enumValue,
    deviceType: LiftType.ELEVATOR,
    label: getDisplayName(key),
  };
}

/**
 * Convert an FbMessageRef back to a LN1 composite key string.
 *
 * The variant is inferred from encodedType:
 *   - OneHotMessage (1) -> "valued"
 *   - BitMaskMessage (2) -> "normal"
 *
 * Returns an empty string when messageContent does not map to a known category.
 */
export function fbRefToCompositeKey(ref: FbMessageRef): string {
  const categoryKey = messageContentToCategoryKey(ref.messageContent as MessageContent);
  if (!categoryKey) return "";
  const variant: "normal" | "valued" =
    ref.encodedType === EncodedMessageContent.OneHotMessage ? "valued" : "normal";
  return `${categoryKey}:${variant}:${ref.messageValue}`;
}
